/**
 * Merko Integrations — per-app `source` schema.
 *
 * The handoff's source table, expressed so the form can be generated from it
 * and a payload checked before it reaches the network. Two prohibitions are
 * enforced here because both are silent data bugs rather than validation
 * errors:
 *
 *   • the static discovery option `{id: "all"}` must never be submitted as a
 *     source — it is a label for "account-wide", and account-wide is `{}`;
 *   • a selected list must never be silently replaced with account-wide `{}`,
 *     so an app that accepts a list keeps it as an explicit choice.
 */

import { MAX_IDENTIFIER_LENGTH } from "./types";
import type { AppId, Source } from "./types";

export type SourceFieldKind = "string" | "decimal-string" | "integer";

export interface SourceField {
    key: string;
    label: string;
    kind: SourceFieldKind;
    required: boolean;
    /** Short help shown under the input. */
    hint?: string;
    /** `integer` only. */
    min?: number;
    max?: number;
    /** Default applied when the user leaves it blank. */
    defaultValue?: number;
    /** True when discovery can offer values for this field. */
    discoverable?: boolean;
    /** A closed set of values to offer. The first of each pair is what is sent. */
    choices?: string[];
    /** Further spellings the backend also accepts for `choices`. */
    acceptedAliases?: string[];
}

export interface AppSourceSchema {
    /** False for an app with no contact import at all (WordPress.com). */
    contactImport: boolean;
    /** True when `{}` is a legitimate account-wide source. */
    accountWide: boolean;
    fields: SourceField[];
    /** One line for the form, straight from the handoff's UI note. */
    note: string;
}

const NONE: SourceField[] = [];

/** Apps whose contacts are account-wide with no selectable source. */
const ACCOUNT_WIDE_ONLY: AppSourceSchema = {
    contactImport: true,
    accountWide: true,
    fields: NONE,
    note: "Account-wide contacts. There is nothing to select.",
};

/** Zoho CRM and Salesforce share one module rule. */
const CRM_MODULE: AppSourceSchema = {
    contactImport: true,
    accountWide: true,
    fields: [
        {
            key: "module",
            label: "Module",
            kind: "string",
            required: false,
            hint: "Contacts or Leads. Defaults to Contacts when left blank.",
            choices: ["Contacts", "Leads"],
            // "`Contact`/`Lead` singular accepted by backend."
            acceptedAliases: ["Contact", "Lead"],
        },
    ],
    note: "Defaults to Contacts. The singular Contact/Lead is also accepted.",
};

export const SOURCE_SCHEMAS: Record<AppId, AppSourceSchema> = {
    mailchimp: {
        contactImport: true,
        accountWide: false,
        fields: [
            {
                key: "list_id",
                label: "Audience",
                kind: "string",
                required: true,
                discoverable: true,
                hint: "The Mailchimp audience (list) to import from.",
            },
        ],
        note: "Pick an audience/list. Imports all member statuses.",
    },

    wordpress: {
        contactImport: false,
        accountWide: false,
        fields: NONE,
        note: "No contact import. Sources are site IDs, used for draft creation only.",
    },

    zoho: CRM_MODULE,
    salesforce: CRM_MODULE,

    googlesheets: {
        contactImport: true,
        accountWide: false,
        fields: [
            { key: "spreadsheet_id", label: "Spreadsheet ID", kind: "string", required: true },
            { key: "sheet_name", label: "Sheet (tab) name", kind: "string", required: true },
            {
                key: "header_row",
                label: "Header row",
                kind: "integer",
                required: false,
                min: 1,
                max: 100,
                defaultValue: 1,
                hint: "Which row holds the column headers. 1–100, default 1.",
            },
        ],
        note: "Enter the spreadsheet ID and tab name explicitly.",
    },

    airtable: {
        contactImport: true,
        accountWide: false,
        fields: [
            { key: "base_id", label: "Base ID", kind: "string", required: true },
            { key: "table_id", label: "Table ID", kind: "string", required: true },
        ],
        note: "Enter the exact IDs — browsing is not implemented.",
    },

    typeform: {
        contactImport: true,
        accountWide: false,
        fields: [
            {
                key: "form_id",
                label: "Form",
                kind: "string",
                required: true,
                discoverable: true,
            },
        ],
        note: "Discovery lists your forms. Map answer field IDs in the mapping step.",
    },

    klaviyo: {
        contactImport: true,
        accountWide: true,
        fields: [
            {
                key: "list_id",
                label: "List",
                kind: "string",
                required: false,
                discoverable: true,
            },
        ],
        note: "Account-wide, or a list you select explicitly.",
    },

    brevo: {
        contactImport: true,
        accountWide: true,
        fields: [
            {
                key: "list_id",
                label: "List",
                kind: "decimal-string",
                required: false,
                discoverable: true,
                hint: "A numeric list ID, sent as a string.",
            },
        ],
        note: "Account-wide or a selected list. A list-specific opt-out applies only to that list.",
    },

    activecampaign: {
        contactImport: true,
        accountWide: true,
        fields: [
            {
                key: "list_id",
                label: "List",
                kind: "decimal-string",
                required: false,
                discoverable: true,
                hint: "A numeric list ID, sent as a string.",
            },
        ],
        note: "Account-wide or a selected list. A list-specific opt-out applies only to that list.",
    },

    kit: {
        contactImport: true,
        accountWide: true,
        fields: [
            {
                key: "form_id",
                label: "Form",
                kind: "decimal-string",
                required: false,
                discoverable: true,
                hint: "A numeric form ID. Names and slugs are not accepted.",
            },
        ],
        note: "Discovered form IDs must stay decimal strings, never names or slugs.",
    },

    mailerlite: {
        contactImport: true,
        accountWide: true,
        fields: [
            {
                key: "group_id",
                label: "Group",
                kind: "string",
                required: false,
                discoverable: true,
            },
        ],
        note: "Account-wide, or an optional selected group.",
    },

    zendesk: ACCOUNT_WIDE_ONLY,
    shopify: ACCOUNT_WIDE_ONLY,
    hubspot: ACCOUNT_WIDE_ONLY,
    gorgias: ACCOUNT_WIDE_ONLY,
    omnisend: ACCOUNT_WIDE_ONLY,
    intercom: ACCOUNT_WIDE_ONLY,
    pipedrive: ACCOUNT_WIDE_ONLY,
};

/** The static option an app without discovery returns. Never a source value. */
export const STATIC_ALL_OPTION_ID = "all";

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F]/;
const DECIMAL = /^[0-9]+$/;

export interface SourceValidation {
    valid: boolean;
    /** Keyed by field, for highlighting the right input. */
    errors: Record<string, string>;
}

/** A nonempty, length-bounded, control-character-free identifier. */
function checkIdentifier(value: string): string | null {
    if (!value) return "This is required.";
    if (value.length > MAX_IDENTIFIER_LENGTH) {
        return `Must be ${MAX_IDENTIFIER_LENGTH} characters or fewer.`;
    }
    if (CONTROL_CHARS.test(value)) return "Contains characters that are not allowed.";
    return null;
}

/**
 * Validate a source payload for an app before it is sent.
 *
 * Rejects unknown keys outright: the handoff allows only the keys listed for
 * that app, and an extra key is a frontend bug rather than something the user
 * can fix.
 */
export function validateSource(appId: AppId, source: Source): SourceValidation {
    const schema = SOURCE_SCHEMAS[appId];
    const errors: Record<string, string> = {};

    if (!schema) {
        return { valid: false, errors: { _: "Unknown app." } };
    }
    if (!schema.contactImport) {
        return { valid: false, errors: { _: "This app does not import contacts." } };
    }

    const allowed = new Set(schema.fields.map((f) => f.key));
    for (const key of Object.keys(source)) {
        if (!allowed.has(key)) {
            errors[key] =
                key === "id"
                    ? 'The discovery option "all" is a label, not a source. Account-wide is an empty source.'
                    : "This field is not accepted for this app.";
        }
    }

    for (const field of schema.fields) {
        const raw = source[field.key];
        const absent = raw === undefined || raw === null || raw === "";

        if (absent) {
            if (field.required) errors[field.key] = "This is required.";
            continue;
        }

        if (field.kind === "integer") {
            const n = typeof raw === "number" ? raw : Number(raw);
            if (!Number.isInteger(n)) errors[field.key] = "Must be a whole number.";
            else if (field.min !== undefined && n < field.min) errors[field.key] = `Minimum is ${field.min}.`;
            else if (field.max !== undefined && n > field.max) errors[field.key] = `Maximum is ${field.max}.`;
            continue;
        }

        const text = String(raw);
        const problem = checkIdentifier(text);
        if (problem) {
            errors[field.key] = problem;
            continue;
        }
        if (field.kind === "decimal-string" && !DECIMAL.test(text)) {
            errors[field.key] = "Must be a numeric ID.";
        }
        if (field.choices && ![...field.choices, ...(field.acceptedAliases ?? [])].includes(text)) {
            errors[field.key] = `Must be ${field.choices.join(" or ")}.`;
        }
    }

    // Account-wide is only legitimate where the app actually offers it.
    if (!schema.accountWide && schema.fields.some((f) => f.required)) {
        const missingAll = schema.fields
            .filter((f) => f.required)
            .every((f) => source[f.key] === undefined || source[f.key] === "");
        if (missingAll && Object.keys(source).length === 0) {
            errors._ = "This app needs a source to be selected.";
        }
    }

    return { valid: Object.keys(errors).length === 0, errors };
}

/**
 * Build the payload to submit from raw form values.
 *
 * Drops blanks so an untouched optional field becomes an absent key rather than
 * an empty string, and applies a documented default (Sheets' header row) only
 * where the handoff states one.
 */
export function buildSource(appId: AppId, values: Record<string, string>): Source {
    const schema = SOURCE_SCHEMAS[appId];
    const source: Source = {};
    if (!schema) return source;

    for (const field of schema.fields) {
        const raw = (values[field.key] ?? "").trim();
        if (!raw) {
            if (field.defaultValue !== undefined && field.required) {
                source[field.key] = field.defaultValue;
            }
            continue;
        }
        source[field.key] = field.kind === "integer" ? Number(raw) : raw;
    }
    return source;
}

/** True when this app can import contacts at all. */
export const supportsContactImport = (appId: AppId): boolean =>
    SOURCE_SCHEMAS[appId]?.contactImport ?? false;
